import { createSelector } from 'reselect';
import { getStudioSlice } from './page';
import { VideoModel, VideoCreatorErrorCode, AppState } from '../../types/store';
import { getCompliantLocalStorageItem } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/selectors';
import { CompliantLocalStorageKeys } from '@wix/da-react-context/pkg/compliantLocalStorage/redux/constants';
import { getDataEntities } from '@wix/da-shared-react/pkg/redux/entities/selectors';
import { PapiVideoCreatorGeneration } from '@wix/da-papi-types';

export const getVideoCreator = createSelector(
  [getStudioSlice],
  state => state?.videoCreator
);

export const getVideoCreatorPrompt = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.prompt ?? ''
);

export const getVideoCreatorModel = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.model ?? VideoModel.HAILUO
);

export const getVideoCreatorAspectRatio = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.aspectRatio ?? '4:3'
);

export const getVideoCreatorDuration = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.duration ?? 6
);

export const getVideoCreatorSound = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.sound ?? false
);

export const getVideoCreatorStatus = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.status ?? 'idle'
);

export const getVideoCreatorReferenceDeviation = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.referenceDeviation
);

export const getVideoCreatorErrorCode = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.errorCode ?? VideoCreatorErrorCode.NO_ERROR
);

export const getVideoCreatorRestrictions = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.restrictions
);

export const getVideoCreatorPointsPackages = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.pointsPackages ?? []
);

export const getVideoCreatorPricePoints = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.pricePoints ?? 0
);

export const getVideoCreatorIsCalculatingCost = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.isCalculatingCost ?? false
);

export const getVideoCreatorPendingGenerations = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.pendingGenerations ?? []
);

export const getVideoCreatorModelsExpanded = createSelector(
  [getVideoCreator],
  videoCreator => videoCreator?.modelsExpanded ?? false
);

export const getVideoCreatorFirstGenModalSeenFromStorage = createSelector(
  [getStudioSlice],
  state =>
    getCompliantLocalStorageItem(
      state,
      CompliantLocalStorageKeys.VIDEO_CREATOR_FIRST_GEN_SEEN
    ) === '1'
);

// Get video generations from entities by IDs
// Dedupe generationids (theoretically this should be taken care of by the stream,
// but this is a failsafe).
export const getVideoGenerationsByIds = createSelector(
  [
    (state: AppState) => state,
    (_: AppState, generationIds: number[]) => generationIds,
  ],
  (state, generationIds): PapiVideoCreatorGeneration[] => {
    const entities = getDataEntities<AppState>(state);
    const videoGenerations = entities?.videoGeneration as
      | Record<number, PapiVideoCreatorGeneration>
      | undefined;
    if (!videoGenerations) {
      return [];
    }
    const seen = new Set<number>();
    const dedupedIds = generationIds.filter(id => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return dedupedIds
      .map(id => videoGenerations[id])
      .filter((video): video is PapiVideoCreatorGeneration => !!video);
  }
);
